import React, { useContext, useEffect, useState } from 'react';
import { Button, Checkbox, Dialog, DialogContent, DialogTitle, FormControlLabel, Grid } from '@material-ui/core';
import { Ruler } from 'lucide-react';

import { warehouseApi as api } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications } from 'shared';

import ChildLocationsModal from './locations/childLocationsModal';
import DownloadLabelsModal from './locations/downloadLabelsModal';
import LocationDimensionsModal from './locations/LocationDimensionsModal';

const YES_VALUE = 'YES';
const NO_VALUE = 'NO';

function ViewLocations({ v2Locations }) {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [tempZones, setTempZones] = useState([]);
    const [types, setTypes] = useState([]);
    const [updatingLocationType, setUpdatingLocationType] = useState(null);
    const [updatingLocationIDs, setUpdatingLocationIDs] = useState([]);
    const [externalFilters, setExternalFilters] = useState({});
    const [activeOnly, setActiveOnly] = useState(true);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [childLocationModalData, setChildLocationModalData] = useState(null);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [dimensionsModalLocation, setDimensionsModalLocation] = useState(null);
    const [pendingDownloadIds, setPendingDownloadIds] = useState([]);

    const user = useContext(UserContext);
    const { roles } = useContext(UserContext);
    const canOffsite = roles?.filter((role) => role.name.endsWith('OFFSITE_TRANSFERS')).length >= 1;

    const productionTypeOptions = [
        { text: 'Both', value: 'BOTH' },
        { text: 'Conventional', value: 'CONVENTIONAL' },
        { text: 'Organic', value: 'ORGANIC' },
    ];
    const inventoryTypeOptions = [
        { text: '', value: null },
        { text: 'Pallet', value: 'Pallet' },
        { text: 'Case', value: 'Case' },
    ];
    const inventorySourceOptions = [
        { text: 'Any', value: 'ANY' },
        { text: 'First Party', value: 'FIRST_PARTY' },
        { text: 'FBM', value: 'FBM' },
    ];

    const canEditField = (accessor, row) => {
        const locationType = types.find((item) => item.type === row.type);
        if (!locationType) {
            return false;
        }
        if (Object.keys(locationType.staticValues).includes(accessor)) {
            return false;
        }

        if (accessor === 'tempZone' && row.type === 'RESERVE') {
            return false;
        }

        return true;
    };

    const columns = [
        // { accessor: "id", Header: "Location ID" },
        { accessor: 'name', Header: 'Name' },
        {
            accessor: 'type',
            Header: 'Type',
            editable: (row) => canEditField('type', row.original),
            // only V1 location types can have their type updated
            editProps: { type: 'select', options: types.filter((item) => item.creatableV1).map((item) => item.type) },
            filterType: 'select',
        },
        {
            accessor: 'active',
            Header: 'Active',
            type: 'checkbox',
            editable: (row) => canEditField('active', row.original),
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'usable',
            Header: 'Sellable',
            type: 'checkbox',
            editable: (row) => canEditField('usable', row.original),
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'tempZone',
            Header: 'Temp Zone',
            editable: (row) => canEditField('tempZone', row.original),
            editProps: { type: 'select', options: tempZones },
            filterType: 'select',
        },
        {
            accessor: 'productionMethod',
            Header: 'OG/CV',
            editable: (row) => canEditField('productionMethod', row.original),
            editProps: { type: 'select', options: productionTypeOptions },
            filterType: 'select',
        },
        {
            accessor: 'inventoryType',
            Header: 'Inventory Type',
            editable: (row) => canEditField('inventoryType', row.original),
            editProps: { type: 'select', options: inventoryTypeOptions },
            filterType: 'select',
        },
        {
            accessor: 'inventorySource',
            Header: 'Inventory Source',
            editable: (row) => canEditField('inventorySource', row.original),
            editProps: { type: 'select', options: inventorySourceOptions },
            filterType: 'select',
        },
        {
            accessor: 'maxPallets',
            Header: 'Max Pallets',
            editable: (row) => canEditField('maxPallets', row.original),
            editProps: { type: 'input', inputType: 'number', integer: true, min: '0' },
        },
        {
            accessor: 'multipleLot',
            Header: 'Multiple Lot',
            type: 'checkbox',
            editable: (row) => canEditField('multipleLot', row.original),
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'multipleSku',
            Header: 'Multiple Sku',
            type: 'checkbox',
            editable: (row) => canEditField('multipleSku', row.original),
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'numChildLocations',
            Header: 'Child Locations',
            Cell: ({ row }) => {
                const numChildLocations = row.original.numChildLocations || 0;
                const isClickable = row.original.type === 'REPLENISHMENT';
                return (
                    <Button
                        disabled={!isClickable}
                        onClick={() => {
                            setChildLocationModalData(row.original);
                        }}
                    >
                        {!isClickable ? <span>0</span> : <strong>{numChildLocations}</strong>}
                    </Button>
                );
            },
            editable: false,
        },
        {
            accessor: 'download',
            Header: 'Label',
            Cell: ({ row }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        onClick={() => {
                            api.bulkDownloadLocationLabels([row.original.id]);
                        }}
                    >
                        Download
                    </Button>
                </div>
            ),
        },

        // hidden columns, used for filtering
        { accessor: 'zone', Header: 'Zone', hiddenColumn: true },
        { accessor: 'aisle', Header: 'Aisle', hiddenColumn: true, filterType: 'includes' },
        { accessor: 'bay', Header: 'Bay', hiddenColumn: true, filterType: 'between' },
        { accessor: 'shelf', Header: 'Shelf', hiddenColumn: true, filterType: 'includes' },
        { accessor: 'position', Header: 'Position', hiddenColumn: true, filterType: 'between' },

        {
            accessor: 'dimensions',
            Header: 'Dimensions',
            Cell: ({ row }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        onClick={() => {
                            setDimensionsModalLocation(row.original);
                        }}
                        size="small"
                        title="Edit dimensions"
                        aria-label="Edit dimensions"
                    >
                        <Ruler size={16} />
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'enableBulkMove',
            Header: 'Enable Bulk Move',
            type: 'checkbox',
            editable: (row) => canOffsite && canEditField('enableBulkMove', row.original),
            editProps: { type: 'checkbox' },
        },
    ];

    const bulkActions = [
        {
            name: 'Download Labels',
            action: async (ids) => {
                const hasLocationsWithChildren = ids.some((id) => {
                    const row = rows.find((row) => row.id === id);
                    return row && row.type === 'REPLENISHMENT' && row.numChildLocations > 0;
                });

                if (hasLocationsWithChildren) {
                    // Show modal to ask about including child locations
                    setPendingDownloadIds(ids);
                    setShowDownloadModal(true);
                    return false;
                }
                // No child locations, download directly
                api.bulkDownloadLocationLabels(ids);
                return true;
            },
        },
    ];

    if (v2Locations) {
        bulkActions.push({
            name: 'Edit Attributes',
            action: async (ids) => {
                const locType = rows.find((item) => item.id === ids[0]).type;
                const canUpdate = ids.every((id) => {
                    const row = rows.find((item) => item.id === id);
                    if (row && row.type === locType) {
                        return true;
                    }
                    return false;
                });
                if (canUpdate) {
                    const locationType = types.find((item) => item.type === locType);
                    if (
                        [
                            'usable',
                            'tempZone',
                            'productionMethod',
                            'active',
                            'inventoryType',
                            'maxPallets',
                            'multipleLot',
                            'multipleSku',
                            'enableBulkMove',
                        ].every((field) => Object.keys(locationType.staticValues).includes(field))
                    ) {
                        setNotification({
                            text: `Location Type ${locType} has no editable attributes`,
                            level: 'error',
                        });
                        return false;
                    }

                    // Open a Bulk Update attribute modal
                    setUpdatingLocationType(locationType);
                    setUpdatingLocationIDs(ids);
                    return true;
                }
                setNotification({ text: 'Cannot bulk update attributes for mixed location types', level: 'error' });
                return false;
            },
        });
    }

    const getReplenishmentChildLocations = async () => {
        const response = await api.getReplenishmentChildLocations(user.getFacilityID());
        if (response.status === false || !response?.data?.locations) {
            setNotification({ text: 'Failed to get replenishment child locations', level: 'error' });
            return;
        }

        const childLocationMap = {};

        for (const location of response.data.locations) {
            if (!childLocationMap[location.parentLocationId]) {
                childLocationMap[location.parentLocationId] = [];
            }
            childLocationMap[location.parentLocationId].push(location);
        }

        return childLocationMap;
    };

    const getLocations = async () => {
        const childLocationMap = await getReplenishmentChildLocations();

        const response = await api.getLocations(user.getFacilityID(), activeOnly);

        if (response.status === false) {
            setNotification({ text: `No locations found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        const rowsWithChildLocationData = response.data.rows.map((item) => {
            item.numChildLocations = childLocationMap[item.id]?.length ?? 0;
            item.childLocations = childLocationMap[item.id] ?? [];
            return item;
        });

        setRows(rowsWithChildLocationData);

        if (childLocationModalData) {
            const row = rowsWithChildLocationData.find((item) => item.id === childLocationModalData.id);
            if (row) {
                setChildLocationModalData(row);
            }
        }

        if (dimensionsModalLocation) {
            const row = rowsWithChildLocationData.find((item) => item.id === dimensionsModalLocation.id);
            if (row) {
                setDimensionsModalLocation(row);
            }
        }
    };

    const getTempZones = async () => {
        const res = await api.getLocationTempZones();
        if (!res.status) {
            setNotification({ text: 'Failed to get location temp zones', level: 'error' });
            return;
        }

        setTempZones(res.data.tempZones);
    };

    const getTypes = async () => {
        const res = await api.getLocationTypesV2(user.getFacilityID());
        if (!res.status) {
            setNotification({ text: 'Failed to get location types', level: 'error' });
            return;
        }

        setTypes(res.data.types);
    };

    const onSaveEdit = async ({ field, id, value }) => {
        const updates = {
            [field]: value,
            ids: [id],
        };
        updateLocations(updates);
    };

    const updateLocations = async (updates) => {
        const response = await api.updateLocations(updates);
        if (response.status === false) {
            let message = 'Error modifying location';
            if (response) {
                message += `: ${response.msg}`;
            }
            setNotification({ text: message, level: 'error' });
            return false;
        }

        let successMessage = 'Locations updated!';

        // If the locations had any child locations, we need to update them too
        if (updates.hasOwnProperty('active')) {
            const childUpdates = {
                active: updates.active,
                ids: [],
            };
            const childLocations = updates.ids
                .map((id) => rows.find((item) => item.id === id))
                .filter((row) => row && row.childLocations)
                .flatMap((row) => row.childLocations);
            for (const childLocation of childLocations) {
                childUpdates.ids.push(childLocation.id);
            }
            if (childUpdates.ids.length > 0) {
                const childResponse = await api.updateLocations(childUpdates);
                if (childResponse.status === false) {
                    setNotification({
                        text: 'Parent locations updated, but error modifying child locations',
                        level: 'error',
                    });
                    return false;
                }
                successMessage = 'Locations updated (with child locations)';
            }
        }

        setNotification({ text: successMessage, level: 'success' });
        getLocations();
        return true;
    };

    useEffect(() => {
        getLocations();
    }, [activeOnly]);

    useEffect(() => {
        getTempZones();
        getTypes();
    }, []);

    const advancedFilterFormFields = [
        { name: 'zone', label: 'Zone', gridValue: 12 },
        { name: 'aisleStart', label: 'Aisle Start', gridValue: 6 },
        { name: 'aisleEnd', label: 'Aisle End', gridValue: 6 },
        { name: 'bayStart', label: 'Bay Start', gridValue: 6 },
        { name: 'bayEnd', label: 'Bay End', gridValue: 6 },
        { name: 'shelfStart', label: 'Shelf Start', gridValue: 6 },
        { name: 'shelfEnd', label: 'Shelf End', gridValue: 6 },
        { name: 'positionStart', label: 'Position Start', gridValue: 6 },
        { name: 'positionEnd', label: 'Position End', gridValue: 6 },
    ];

    const handleFilterFormDataChange = (formData) => {
        const validLetters = getLettersInRange('A', 'ZZ');
        const ext = {};
        ext.zone = formData.zone;
        if (formData.aisleStart) {
            let aisleRange = [formData.aisleStart.toUpperCase()];
            if (formData.aisleEnd) {
                aisleRange = validLetters.slice(
                    validLetters.indexOf(formData.aisleStart.toUpperCase()),
                    validLetters.indexOf(formData.aisleEnd.toUpperCase()) + 1,
                );
            }
            ext.aisle = aisleRange.length > 0 ? aisleRange : '';
        } else {
            ext.aisle = '';
        }
        if (formData.bayStart) {
            const bayRange = [Number(formData.bayStart)];
            bayRange.push(formData.bayEnd ? Number(formData.bayEnd) : Number(formData.bayStart));
            ext.bay = bayRange;
        } else {
            ext.bay = '';
        }
        if (formData.shelfStart) {
            let shelfRange = [formData.shelfStart.toUpperCase()];
            if (formData.shelfEnd) {
                shelfRange = validLetters.slice(
                    validLetters.indexOf(formData.shelfStart.toUpperCase()),
                    validLetters.indexOf(formData.shelfEnd.toUpperCase()) + 1,
                );
            }
            ext.shelf = shelfRange.length > 0 ? shelfRange : '';
        } else {
            ext.shelf = '';
        }
        if (formData.positionStart) {
            const positionRange = [Number(formData.positionStart)];
            positionRange.push(formData.positionEnd ? Number(formData.positionEnd) : Number(formData.positionStart));
            ext.position = positionRange;
        } else {
            ext.position = '';
        }
        setExternalFilters(ext);
    };

    return (
        <div>
            <Notifications options={notification} />
            {updatingLocationType && (
                <BulkUpdateModal
                    locationType={updatingLocationType}
                    locationIDs={updatingLocationIDs}
                    tempZones={tempZones}
                    productionTypeOptions={productionTypeOptions}
                    inventoryTypeOptions={inventoryTypeOptions}
                    inventorySourceOptions={inventorySourceOptions}
                    setNotification={setNotification}
                    closeModal={() => {
                        setUpdatingLocationType(null);
                    }}
                    updateLocations={updateLocations}
                />
            )}
            <ChildLocationsModal
                parentLocation={childLocationModalData}
                isOpen={!!childLocationModalData}
                onClose={() => setChildLocationModalData(null)}
                onChildLocationUpdated={getLocations}
            />
            <LocationDimensionsModal
                location={dimensionsModalLocation}
                isOpen={!!dimensionsModalLocation}
                onClose={() => setDimensionsModalLocation(null)}
                onUpdated={getLocations}
            />
            <DownloadLabelsModal
                isOpen={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
                pendingDownloadIds={pendingDownloadIds}
                rows={rows}
                onComplete={() => {
                    setShowDownloadModal(false);
                    setPendingDownloadIds([]);
                }}
            />
            <Grid container>
                {v2Locations && (
                    <Grid item xs={12}>
                        <Button
                            onClick={() => {
                                setShowAdvancedFilters(!showAdvancedFilters);
                            }}
                            style={{ float: 'right', margin: 0, padding: 0 }}
                            size="small"
                        >
                            {showAdvancedFilters ? 'Hide Filters' : 'Reserve Location Filters'}
                        </Button>
                    </Grid>
                )}
                <Grid item xs={showAdvancedFilters ? 9 : 12}>
                    <h3>Warehouse Locations</h3>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={(event) => {
                                        setActiveOnly(!event.target.checked);
                                    }}
                                    color="primary"
                                    checked={!activeOnly}
                                />
                            }
                            label="Show Deactivated Locations"
                        />
                    </Grid>

                    <DataTable
                        selectMultiple
                        columns={columns}
                        data={rows}
                        editable
                        saveEdit={onSaveEdit}
                        bulkActions={bulkActions}
                        externalFilters={externalFilters}
                    />
                </Grid>
                {showAdvancedFilters && (
                    <Grid item xs={3}>
                        <div
                            style={{
                                marginLeft: '10px',
                                paddingLeft: '10px',
                                backgroundColor: '#e8f0ff',
                                height: '100%',
                            }}
                        >
                            <h3>Reserve Location Filters</h3>
                            <Button
                                onClick={() => {
                                    handleFilterFormDataChange({});
                                    setShowAdvancedFilters(false);
                                }}
                            >
                                Clear External Filters
                            </Button>
                            <FormComponent
                                compact
                                formFields={advancedFilterFormFields}
                                onSubmit={handleFilterFormDataChange}
                                button={{ gridValue: 12 }}
                            />
                        </div>
                    </Grid>
                )}
            </Grid>
        </div>
    );
}

function BulkUpdateModal({
    closeModal,
    inventorySourceOptions,
    inventoryTypeOptions,
    locationIDs,
    locationType,
    productionTypeOptions,
    setNotification,
    tempZones,
    updateLocations,
}) {
    const formFields = [
        {
            name: 'attribute',
            label: 'Attribute',
            gridValue: 12,
            inputElement: 'select',
            required: true,
            inputProps: {
                opts: [
                    { text: 'Active', value: 'active' },
                    { text: 'Sellable', value: 'usable' },
                    { text: 'Temp Zone', value: 'tempZone' },
                    { text: 'OG/CV', value: 'productionMethod' },
                    { text: 'Inventory Type', value: 'inventoryType' },
                    { text: 'Inventory Source', value: 'inventorySource' },
                    { text: 'Max Pallets', value: 'maxPallets' },
                    { text: 'Multiple Lot', value: 'multipleLot' },
                    { text: 'Multiple Sku', value: 'multipleSku' },
                ].filter((item) => !Object.keys(locationType.staticValues).includes(item.value)),
            },
        },
        {
            name: 'active',
            label: 'Active',
            required: true,
            display: (formData) => formData.attribute === 'active',
            inputElement: 'select',
            inputProps: { opts: [YES_VALUE, NO_VALUE] },
        },
        {
            name: 'usable',
            label: 'Sellable',
            required: true,
            display: (formData) => formData.attribute === 'usable',
            inputElement: 'select',
            inputProps: { opts: [YES_VALUE, NO_VALUE] },
        },
        {
            name: 'tempZone',
            label: 'Temp Zone',
            required: true,
            display: (formData) => formData.attribute === 'tempZone',
            inputElement: 'select',
            inputProps: { opts: tempZones },
        },
        {
            name: 'productionMethod',
            label: 'OG/CV',
            required: true,
            display: (formData) => formData.attribute === 'productionMethod',
            inputElement: 'select',
            inputProps: { opts: productionTypeOptions },
        },
        {
            name: 'inventoryType',
            label: 'Inventory Type',
            required: true,
            display: (formData) => formData.attribute === 'inventoryType',
            inputElement: 'select',
            inputProps: { opts: inventoryTypeOptions },
        },
        {
            name: 'inventorySource',
            label: 'Inventory Source',
            required: true,
            display: (formData) => formData.attribute === 'inventorySource',
            inputElement: 'select',
            inputProps: { opts: inventorySourceOptions },
        },
        {
            name: 'maxPallets',
            label: 'Max Pallets',
            required: true,
            display: (formData) => formData.attribute === 'maxPallets',
            inputElement: 'textField',
            inputProps: { required: true, type: 'number', integer: true },
        },
        {
            name: 'multipleLot',
            label: 'multiple Lot',
            required: true,
            display: (formData) => formData.attribute === 'multipleLot',
            inputElement: 'select',
            inputProps: { opts: [YES_VALUE, NO_VALUE] },
        },
        {
            name: 'multipleSku',
            label: 'multiple Sku',
            required: true,
            display: (formData) => formData.attribute === 'multipleSku',
            inputElement: 'select',
            inputProps: { opts: [YES_VALUE, NO_VALUE] },
        },
    ];

    const submitBulkUpdate = async (formData) => {
        let value = formData[formData.attribute];
        if (value === YES_VALUE) {
            value = true;
        } else if (value === NO_VALUE) {
            value = false;
        }
        const sendParams = {
            ids: locationIDs,
            [formData.attribute]: value,
        };

        const response = await updateLocations(sendParams);
        if (!response) {
            return;
        }
        closeModal();
    };

    return (
        <Dialog open onClose={closeModal} fullWidth maxWidth="lg">
            <DialogTitle>Bulk Update Attributes for {locationIDs.length} locations</DialogTitle>
            <DialogContent style={{ paddingTop: '0px' }}>
                <FormComponent formFields={formFields} onSubmit={submitBulkUpdate} />
                <br />
                <br />
            </DialogContent>
        </Dialog>
    );
}

function nextChar(c) {
    const u = c.toUpperCase();
    if (same(u, 'Z')) {
        let txt = '';
        let i = u.length;
        while (i--) {
            txt += 'A';
        }
        return `${txt}A`;
    }
    let p = '';
    let q = '';
    if (u.length > 1) {
        p = u.substring(0, u.length - 1);
        q = String.fromCharCode(p.slice(-1).charCodeAt(0));
    }
    const l = u.slice(-1).charCodeAt(0);
    const z = nextLetter(l);
    if (z === 'A') {
        return p.slice(0, -1) + nextLetter(q.slice(-1).charCodeAt(0)) + z;
    }
    return p + z;
}

let getLettersInRange = (start, end) => {
    const range = [start];
    let cur = start;
    while (cur != end) {
        cur = nextChar(cur);
        range.push(cur);
    }
    return range;
};

function nextLetter(l) {
    if (l < 90) {
        return String.fromCharCode(l + 1);
    }
    return 'A';
}

function same(str, char) {
    let i = str.length;
    while (i--) {
        if (str[i] !== char) {
            return false;
        }
    }
    return true;
}

export { ViewLocations };
